/**
 * Canonical facet vocabularies (CMS-driven-collections contract §6). These are the EXACT
 * keys the public site filters on (`doc->>'<facet>'`) — the admin must store these values,
 * not free text, or the public filters silently miss the item. Keep this in sync with the
 * backend contract (`be-tepco/docs/cms-driven-collections-backend.md` §6).
 *
 * `industry` (projects) and `family` (products) are NOT listed here: they reference other
 * collections by slug and are resolved dynamically via the `x-ref-slug` widget, so adding a
 * new industry/family needs no code change.
 */

export interface VocabOption {
  /** Stored canonical value. */
  value: string;
  /** i18n key for the human-facing label. */
  labelKey: string;
}

/** Article category keys (`articles.category_key`). */
export const ARTICLE_CATEGORY_KEYS: VocabOption[] = [
  { value: 'technical', labelKey: 'vocab.category.technical' },
  { value: 'caseStudy', labelKey: 'vocab.category.caseStudy' },
  { value: 'companyNews', labelKey: 'vocab.category.companyNews' },
  { value: 'industryAnalysis', labelKey: 'vocab.category.industryAnalysis' },
  { value: 'pressRelease', labelKey: 'vocab.category.pressRelease' },
];

/** Optional editorial flag on articles (`articles.flag`). */
export const ARTICLE_FLAGS: VocabOption[] = [
  { value: 'vision2030', labelKey: 'vocab.flag.vision2030' },
  { value: 'sustainability', labelKey: 'vocab.flag.sustainability' },
];

/** Job posting departments (`job-postings.department`). */
export const JOB_DEPARTMENTS: VocabOption[] = [
  { value: 'engineering', labelKey: 'vocab.department.engineering' },
  { value: 'commercial', labelKey: 'vocab.department.commercial' },
  { value: 'manufacturing', labelKey: 'vocab.department.manufacturing' },
  { value: 'graduate', labelKey: 'vocab.department.graduate' },
];

/**
 * Subsidiary tier (`subsidiaries.tier`) — the render role on the public site: the
 * parent brand, the KSA flagship, or a specialist subsidiary. Mirrors the backend
 * `subsidiaryDocSchema` enum (docschema/schemas.go).
 */
export const SUBSIDIARY_TIERS: VocabOption[] = [
  { value: 'parent', labelKey: 'vocab.subsidiaryTier.parent' },
  { value: 'flagship', labelKey: 'vocab.subsidiaryTier.flagship' },
  { value: 'specialist', labelKey: 'vocab.subsidiaryTier.specialist' },
];

/**
 * Team-member group (`team-members.group`) — the about-page surface a person
 * renders on: the Board register, the Group CEO feature card, or the executive
 * band. Mirrors the backend `teamMemberDocSchema` enum (docschema/schemas.go).
 */
export const TEAM_GROUPS: VocabOption[] = [
  { value: 'board', labelKey: 'vocab.teamGroup.board' },
  { value: 'ceo', labelKey: 'vocab.teamGroup.ceo' },
  { value: 'executive', labelKey: 'vocab.teamGroup.executive' },
];

/**
 * Industry icon (`industries.doc.icon_key`) — the house line-icon an editor
 * picks for a sector when no custom image is uploaded. The values are the icon
 * names the public site resolves against `INDUSTRY_ICON_BY_NAME`
 * (tepco/web/src/components/icons/IndustryIcons.tsx); keep the two lists in sync.
 */
export const INDUSTRY_ICONS: VocabOption[] = [
  { value: 'flame', labelKey: 'vocab.industryIcon.flame' },
  { value: 'tower', labelKey: 'vocab.industryIcon.tower' },
  { value: 'factory', labelKey: 'vocab.industryIcon.factory' },
  { value: 'hospital', labelKey: 'vocab.industryIcon.hospital' },
  { value: 'building', labelKey: 'vocab.industryIcon.building' },
  { value: 'shield', labelKey: 'vocab.industryIcon.shield' },
  { value: 'sun', labelKey: 'vocab.industryIcon.sun' },
  { value: 'route', labelKey: 'vocab.industryIcon.route' },
];

/** Book-a-call topic (`call_requests.topic`) — mirrors the public `book-a-call` form (PHASE 06). */
export const CALL_TOPICS: VocabOption[] = [
  { value: 'proposal', labelKey: 'vocab.callTopic.proposal' },
  { value: 'evidence', labelKey: 'vocab.callTopic.evidence' },
  { value: 'prequal', labelKey: 'vocab.callTopic.prequal' },
  { value: 'local_content', labelKey: 'vocab.callTopic.local_content' },
  { value: 'site_services', labelKey: 'vocab.callTopic.site_services' },
  { value: 'plant_visit', labelKey: 'vocab.callTopic.plant_visit' },
  { value: 'other', labelKey: 'vocab.callTopic.other' },
];

/** Book-a-call preferred time window, Riyadh time (`call_requests.slot`). */
export const CALL_SLOTS: VocabOption[] = [
  { value: '0810', labelKey: 'vocab.callSlot.0810' },
  { value: '1012', labelKey: 'vocab.callSlot.1012' },
  { value: '1315', labelKey: 'vocab.callSlot.1315' },
  { value: '1517', labelKey: 'vocab.callSlot.1517' },
  { value: 'other', labelKey: 'vocab.callSlot.other' },
];

/** Book-a-call contact channel (`call_requests.channel`). */
export const CALL_CHANNELS: VocabOption[] = [
  { value: 'phone', labelKey: 'vocab.callChannel.phone' },
  { value: 'whatsapp', labelKey: 'vocab.callChannel.whatsapp' },
  { value: 'teams', labelKey: 'vocab.callChannel.teams' },
  { value: 'zoom', labelKey: 'vocab.callChannel.zoom' },
];

/**
 * Contact inquiry type (`contact_submissions.inquiry_type`), extended (PHASE 06) with the
 * legacy values (`ir`, `sustainability`, `press`) older submissions still carry so the
 * filter and detail label resolve for them too, even though the public form no longer
 * offers them.
 */
export const CONTACT_TYPES: VocabOption[] = [
  { value: 'commercial', labelKey: 'vocab.contactType.commercial' },
  { value: 'engineering', labelKey: 'vocab.contactType.engineering' },
  { value: 'fabrication', labelKey: 'vocab.contactType.fabrication' },
  { value: 'services', labelKey: 'vocab.contactType.services' },
  { value: 'prequalification', labelKey: 'vocab.contactType.prequalification' },
  { value: 'careers', labelKey: 'vocab.contactType.careers' },
  { value: 'other', labelKey: 'vocab.contactType.other' },
  { value: 'ir', labelKey: 'vocab.contactType.ir' },
  { value: 'sustainability', labelKey: 'vocab.contactType.sustainability' },
  { value: 'press', labelKey: 'vocab.contactType.press' },
];

/** Which public form a contact submission came through (`contact_submissions.source_form`). */
export const SOURCE_FORMS: VocabOption[] = [
  { value: 'contact', labelKey: 'vocab.sourceForm.contact' },
  { value: 'services', labelKey: 'vocab.sourceForm.services' },
];

/**
 * Where a lead came in (`source` on every lead table): the public site's forms, or the
 * website chat through the integration API (TICKET-002). Values outside this list are shown
 * raw, never dropped (see `features/leads/source.ts`).
 */
export const LEAD_SOURCES: VocabOption[] = [
  { value: 'website', labelKey: 'vocab.leadSource.website' },
  { value: 'chatbot', labelKey: 'vocab.leadSource.chatbot' },
];

/** Job-application region (`job_applications.region`). */
export const APPLICATION_REGIONS: VocabOption[] = [
  { value: 'riyadh', labelKey: 'vocab.applicationRegion.riyadh' },
  { value: 'eastern', labelKey: 'vocab.applicationRegion.eastern' },
  { value: 'western', labelKey: 'vocab.applicationRegion.western' },
  { value: 'madinah', labelKey: 'vocab.applicationRegion.madinah' },
  { value: 'abroad', labelKey: 'vocab.applicationRegion.abroad' },
];

/** Build the `x-enum-labels` map a JSON-schema enum field uses to show friendly text. */
// ── System-B vocabularies (PHASE 04 doc schemas) ─────────────────────────────
// Each mirrors a backend closed list (be-tepco internal/docschema/schemas.go and
// internal/domain/content/configs.go). The label keys are exactly the
// `vocab.<vocab>.<value>` keys the served schema's `x-enum-labels` name, so the
// served and the offline-fallback schemas resolve the same labels.

/** Build a vocabulary whose label keys follow the backend `vocab.<name>.<value>` convention. */
function vocab(name: string, values: readonly string[]): VocabOption[] {
  return values.map((value) => ({ value, labelKey: `vocab.${name}.${value}` }));
}

/** Credential plate group (`credentials.group`, also a list facet). */
export const CREDENTIAL_GROUPS = vocab('credentialGroup', [
  'registration',
  'licence',
  'standard',
  'permit',
  'programme',
  'capital',
  'evidence',
]);

/** Credential status — a legal claim; the schema carries the "confirm first" hint. */
export const CREDENTIAL_STATUSES = vocab('credentialStatus', [
  'current',
  'held',
  'under_application',
  'issued_on_request',
  'as_registered',
  'active',
  'expired',
]);

/** Page blocks a credential plate renders in (`credentials.placements`, list facet `placement`). */
export const CREDENTIAL_PLACEMENTS = vocab('credentialPlacement', [
  'home_plates',
  'documentation_plates',
  'documentation_standards',
  'documentation_local_content',
  'documentation_programmes',
  'investor_plates',
  'sustainability_plates',
  'manufacturing_figures',
]);

/** Product category key (`products.family`): lv / mv / smart-grid. */
export const PRODUCT_FAMILIES = vocab('productFamily', ['lv', 'mv', 'smart-grid']);

/** Project fact key (`projects.facts[].key`). */
export const PROJECT_FACT_KEYS = vocab('projectFact', [
  'client',
  'scope',
  'location',
  'date',
  'value',
  'status',
]);

/** Project fact state — provisional/withheld facts render marked on the site. */
export const FACT_STATES = vocab('factState', ['confirmed', 'provisional', 'withheld']);

/** Article reviewer state. */
export const REVIEWER_STATES = vocab('reviewerState', ['confirmed', 'provisional']);

/** Office / plant kind (`locations.kind`); manufacturing/sales are legacy values. */
export const LOCATION_KINDS = vocab('locationKind', ['plant', 'office', 'manufacturing', 'sales']);

/**
 * Every vocabulary the console renders labels for — the i18n completeness test walks this
 * so a new value without an EN or AR label fails CI.
 */
export const ALL_VOCABS: Record<string, VocabOption[]> = {
  ARTICLE_CATEGORY_KEYS,
  ARTICLE_FLAGS,
  JOB_DEPARTMENTS,
  SUBSIDIARY_TIERS,
  TEAM_GROUPS,
  INDUSTRY_ICONS,
  CALL_TOPICS,
  CALL_SLOTS,
  CALL_CHANNELS,
  CONTACT_TYPES,
  SOURCE_FORMS,
  LEAD_SOURCES,
  APPLICATION_REGIONS,
  CREDENTIAL_GROUPS,
  CREDENTIAL_STATUSES,
  CREDENTIAL_PLACEMENTS,
  PRODUCT_FAMILIES,
  PROJECT_FACT_KEYS,
  FACT_STATES,
  REVIEWER_STATES,
  LOCATION_KINDS,
};

export function enumLabels(options: VocabOption[]): Record<string, string> {
  return Object.fromEntries(options.map((o) => [o.value, o.labelKey]));
}

/** Build `{ value, label }` option list for a plain Select, resolving labels through t(). */
export function vocabSelectOptions(
  options: VocabOption[],
  t: (key: string) => string,
): { value: string; label: string }[] {
  return options.map((o) => ({ value: o.value, label: t(o.labelKey) }));
}
